import type { ReviewSection } from "@workerstation/shared";

export type ReviewCommandGroup = "Sections" | "Navigation" | "Review";

export interface ReviewCommandDefinition {
  readonly id: string;
  readonly group: ReviewCommandGroup;
  readonly label: string;
  readonly shortcut: string;
  readonly ariaKeyShortcuts: readonly string[];
  readonly section?: ReviewSection;
  readonly keywords: readonly string[];
}

export const REVIEW_COMMAND_DEFINITIONS = [
  {
    id: "section.summary",
    group: "Sections",
    label: "Open Summary",
    shortcut: "Alt+1",
    ariaKeyShortcuts: ["Alt+1"],
    section: "summary",
    keywords: ["readiness", "gate", "overview"]
  },
  {
    id: "section.changes",
    group: "Sections",
    label: "Open Changes",
    shortcut: "Alt+2",
    ariaKeyShortcuts: ["Alt+2"],
    section: "changes",
    keywords: ["files", "diff"]
  },
  {
    id: "section.conflicts",
    group: "Sections",
    label: "Open Conflicts",
    shortcut: "Alt+3",
    ariaKeyShortcuts: ["Alt+3"],
    section: "conflicts",
    keywords: ["merge", "resolve"]
  },
  {
    id: "section.findings",
    group: "Sections",
    label: "Open Findings",
    shortcut: "Alt+4",
    ariaKeyShortcuts: ["Alt+4"],
    section: "findings",
    keywords: ["review", "issues"]
  },
  {
    id: "section.checks",
    group: "Sections",
    label: "Open Checks",
    shortcut: "Alt+5",
    ariaKeyShortcuts: ["Alt+5"],
    section: "checks",
    keywords: ["tests", "receipts"]
  },
  {
    id: "section.reviews",
    group: "Sections",
    label: "Open Reviews",
    shortcut: "Alt+6",
    ariaKeyShortcuts: ["Alt+6"],
    section: "reviews",
    keywords: ["specialists", "coverage"]
  },
  {
    id: "section.activity",
    group: "Sections",
    label: "Open Activity",
    shortcut: "Alt+7",
    ariaKeyShortcuts: ["Alt+7"],
    section: "activity",
    keywords: ["events", "runs", "milestones"]
  },
  {
    id: "hunk.next",
    group: "Navigation",
    label: "Next diff hunk",
    shortcut: "F7",
    ariaKeyShortcuts: ["F7"],
    keywords: ["change", "difference"]
  },
  {
    id: "hunk.previous",
    group: "Navigation",
    label: "Previous diff hunk",
    shortcut: "Shift+F7",
    ariaKeyShortcuts: ["Shift+F7"],
    keywords: ["change", "difference"]
  },
  {
    id: "finding.next",
    group: "Navigation",
    label: "Next finding",
    shortcut: "N",
    ariaKeyShortcuts: ["N"],
    keywords: ["issue", "review"]
  },
  {
    id: "finding.previous",
    group: "Navigation",
    label: "Previous finding",
    shortcut: "Shift+N",
    ariaKeyShortcuts: ["Shift+N"],
    keywords: ["issue", "review"]
  },
  {
    id: "focus.return",
    group: "Navigation",
    label: "Return focus to active review section",
    shortcut: "Shift+Escape",
    ariaKeyShortcuts: ["Shift+Escape"],
    keywords: ["back", "tab", "navigation"]
  },
  {
    id: "file.mark-reviewed",
    group: "Review",
    label: "Mark selected file reviewed",
    shortcut: "M",
    ariaKeyShortcuts: ["M"],
    keywords: ["checkpoint", "complete"]
  },
  {
    id: "gate.submit",
    group: "Review",
    label: "Run publication gate action",
    shortcut: "Ctrl/⌘+Enter",
    ariaKeyShortcuts: ["Control+Enter", "Meta+Enter"],
    keywords: ["submit", "publish", "approve"]
  }
] as const satisfies readonly ReviewCommandDefinition[];

export type ReviewCommandId =
  (typeof REVIEW_COMMAND_DEFINITIONS)[number]["id"];
export type ReviewShortcutCommand = ReviewCommandId | "palette.open";

export interface ReviewShortcutInput {
  readonly key: string;
  readonly scopeActive: boolean;
  readonly editable: boolean;
  readonly altKey?: boolean;
  readonly ctrlKey?: boolean;
  readonly metaKey?: boolean;
  readonly shiftKey?: boolean;
  readonly repeat?: boolean;
  readonly composing?: boolean;
}

export const REVIEW_PALETTE_ARIA_KEY_SHORTCUTS = Object.freeze([
  "Control+Shift+P",
  "Meta+Shift+P"
]);

export const REVIEW_WORKBENCH_ARIA_KEY_SHORTCUTS = Object.freeze([
  ...REVIEW_PALETTE_ARIA_KEY_SHORTCUTS,
  ...REVIEW_COMMAND_DEFINITIONS.flatMap(
    (definition) => definition.ariaKeyShortcuts
  )
]).join(" ");

const SECTION_COMMAND_BY_KEY = Object.freeze({
  "1": "section.summary",
  "2": "section.changes",
  "3": "section.conflicts",
  "4": "section.findings",
  "5": "section.checks",
  "6": "section.reviews",
  "7": "section.activity"
} as const);

/** Resolve only Review-scoped keystrokes after the caller classifies focus. */
export function reviewCommandFromShortcut(
  input: ReviewShortcutInput
): ReviewShortcutCommand | undefined {
  if (
    !input.scopeActive ||
    input.editable ||
    input.composing ||
    typeof input.key !== "string"
  ) {
    return undefined;
  }
  const alt = input.altKey === true;
  const control = input.ctrlKey === true;
  const meta = input.metaKey === true;
  const shift = input.shiftKey === true;
  const primary = control !== meta && (control || meta);
  const key = input.key.length === 1 ? input.key.toLowerCase() : input.key;

  if (primary && shift && !alt && key === "p") {
    return input.repeat ? undefined : "palette.open";
  }
  if (alt && !control && !meta && !shift) {
    return SECTION_COMMAND_BY_KEY[
      key as keyof typeof SECTION_COMMAND_BY_KEY
    ];
  }
  if (!alt && !control && !meta && key === "F7") {
    return shift ? "hunk.previous" : "hunk.next";
  }
  if (!alt && !control && !meta && key === "n") {
    return shift ? "finding.previous" : "finding.next";
  }
  if (!alt && !control && !meta && !shift && key === "m") {
    return input.repeat ? undefined : "file.mark-reviewed";
  }
  if (primary && !alt && !shift && key === "Enter") {
    return input.repeat ? undefined : "gate.submit";
  }
  if (!alt && !control && !meta && shift && key === "Escape") {
    return input.repeat ? undefined : "focus.return";
  }
  return undefined;
}

/** Deterministically wrap next/previous selection through durable identities. */
export function adjacentReviewIdentity(
  identities: readonly string[],
  current: string | undefined,
  direction: 1 | -1
): string | undefined {
  if (!Array.isArray(identities) || identities.length === 0) return undefined;
  if (direction !== 1 && direction !== -1) {
    throw new TypeError("Review navigation direction is invalid.");
  }
  if (
    identities.some((identity) =>
      typeof identity !== "string" || identity.length === 0
    ) ||
    new Set(identities).size !== identities.length
  ) {
    throw new TypeError("Review navigation identities must be unique strings.");
  }
  const currentIndex = current === undefined ? -1 : identities.indexOf(current);
  if (currentIndex < 0) {
    return direction === 1 ? identities[0] : identities.at(-1);
  }
  return identities[(currentIndex + direction + identities.length) % identities.length];
}
